/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import memoize from 'memoize-one';
import {
  func, object, number, string, arrayOf, bool,
} from 'prop-types';
import cn from 'classnames';
import _uniqBy from 'lodash/uniqBy';
import _find from 'lodash/find';
import _orderBy from 'lodash/orderBy';
import { IconButton, Tooltip } from '@picsio/ui';
import {
  RewindLeft,
  RewindRight,
  Subtitle,
  Scissors,
  Fullscreen,
} from '@picsio/icons';
import DT from 'duration-time-conversion';
import picsioConfig from '../../../../../../../config';
import * as utils from '../../../../shared/utils';
import Logger from '../../../../services/Logger';
import localization from '../../../../shared/strings';
import uiBlocker from '../../../../services/UiBlocker';
import { parseTime, LocalStorage } from '../../../../shared/utils';
import Icon from '../../../Icon';
import ua from '../../../../ua';
import {
  makeVideoScreenshot,
  isScreenshotSupported,
  makeScreenshotName,
  getVideoQualities,
  QUALITIES_IN_PROGRESS,
  getSavedCurrentTime,
  saveVideoCurrentTime,
  MEDIA_ERRORS,
} from './helpers';
import Crop from './Crop';
import VideoError from './Error';
import Spinner from '../Spinner';
import Dialog, { showErrorDialog } from '../../../dialog';
import MenuOnHover from '../../../MenuOnHover';
import VideoSlider from '../../../VideoSlider';
import uploadThumbail from '../../../../helpers/assets/uploadThumbail';
import TooltipSubtitle from './TooltipSubtitle/TooltipSubtitle';
import PlayerGearMenu from './PlayerGearMenu';
import PlayerThumbnailMenu from './PlayerThumbnailMenu';
import { LOCAL_STORAGE_VIEW_KEY, CHANGE_VIEW_EVENT } from '../../../TranscriptPanel/components/TranscriptList/TranscriptList';
import OcrMarkers from './OcrMarkers';
import KeywordsMarkers from './KeywordsMarkers';
import ActionsMarkers from './ActionsMarkers';

const MP4 = 'video/mp4';
const RETRIEVE_INTERVAL = 5 * 60 * 1000; // 5 min
const QUALITIES_STATUS = {
  initial: 'Video quality',
  processing: 'Please wait, video is processing...',
  unavailable: 'Quality change is not available at the moment',
};

const isMobile = ua.browser.isNotDesktop() || ua.isMobileApp();

const normalizeChapters = (chapters) => chapters.map((chapter) => ({
  ...chapter,
  startTime: DT.t2d(chapter.startTime),
  endTime: chapter.endTime && DT.t2d(chapter.endTime),
}));

const makeInitialState = (assetId, chapters) => {
  const currentTime = getSavedCurrentTime(assetId) || 0;
  return {
    videoError: null,
    isVideoReady: false,
    isDiffVideoReady: false,
    isVideoPlaying: false,
    isVideoWaiting: false,
    isDiffVideoPlaying: false,
    isDiffVideoWaiting: false,
    currentTime,
    diffCurrentTime: 0,
    duration: 0,
    diffDuration: 0,
    volume: typeof LocalStorage.get('volume') === 'number' ? LocalStorage.get('volume') : 0.7,
    isMuted: LocalStorage.get('muted'),
    isDiffMuted: true,
    progress: 0,
    diffProgress: 0,
    timeHovered: {},
    isScreenshotInProgress: false,
    isCustomThumbnailInProgress: false,
    showCrop: false,
    qualities: [],
    chapters: chapters ? normalizeChapters(chapters) : null,
    qualitiesStatus: QUALITIES_STATUS.initial,
    currentQuality: null,
    showInitialThumbnail: true,
    showError: false,
    playbackRate: 1,
    currentVideoTimecode: utils.LocalStorage.get('picsio.timecodeType') || 'Standard',
    showSubtitle: LocalStorage.get('subtitle') || false,
    videoAiView: LocalStorage.get(LOCAL_STORAGE_VIEW_KEY),
  };
};

class Player extends React.Component {
  state = makeInitialState(this.props.data._id, this.props.data.chapters);

  isMainApp = picsioConfig.isMainApp;

  saveCurrentTimeInterval = null;

  $container = React.createRef();

  $video = React.createRef();

  $diffVideo = React.createRef();

  $videoControls = React.createRef();

  $diffVideoControls = React.createRef();

  $dialogWrapper = React.createRef();

  $commentsRangeWrapper = React.createRef();

  $hoveredTracks = {
    main: React.createRef(),
    diff: React.createRef(),
  };

  $hoveredTrackTimes = {
    main: React.createRef(),
    diff: React.createRef(),
  };

  $bufferedTracks = {
    main: React.createRef(),
    diff: React.createRef(),
  };

  $fileInput = React.createRef();

  // Flag to indicate if the user is currently dragging the comment range slider
  isDraggingCommentRange = false;

  /** needs for skip update volume after set muted, native player set volume to 1 by default */
  muteSetted = false;

  indicatorTimeout = null;

  cachedSrcData = {};

  uploadSizeLimit = 5 * 1024000;

  memoizedNormalizeQualitiesList = memoize((qualities) => {
    if (!qualities.length) return [];

    return qualities.map((item) => ({
      text: `${item.quality}p`,
      value: item.quality,
    }));
  });

  componentDidMount() {
    const { state } = this;
    this.mounted = true;
    this.getQualities();
    /** Set initial values */
    const {
      isMuted, volume, currentTime, showSubtitle,
    } = state;
    this.$video.current.volume = volume;
    this.$video.current.muted = isMuted;
    this.$video.current.currentTime = currentTime;
    if (this.$video.current?.textTracks[0]) {
      this.$video.current.textTracks[0].mode = showSubtitle ? 'showing' : 'hidden';
    }

    /** Add event listeners */
    this.$video.current.addEventListener('play', this.handlePlay);
    this.$video.current.addEventListener('pause', this.handlePause);
    this.$video.current.addEventListener('timeupdate', this.handleTimeUpdate);
    this.$video.current.addEventListener('volumechange', this.handleVolumeChange);
    this.$video.current.addEventListener('waiting', this.handleWaiting);
    this.$video.current.addEventListener('playing', this.handlePlaying);
    this.$video.current.addEventListener('progress', this.handleProgress);
    this.$video.current.addEventListener('ratechange', this.handleRateChange);

    // show default video controls for iOS app. It allows to make fullscreen video.
    if (isMobile && !this.$diffVideo.current) { this.$video.current.setAttribute('controls', 'controls'); }

    if (this.$diffVideo.current) {
      this.addListenersToDiffVideo();
      if (this.$diffVideo.current?.textTracks[0]) {
        this.$diffVideo.current.textTracks[0].mode = showSubtitle ? 'showing' : 'hidden';
      }
    }

    document.body.addEventListener('keydown', this.keyDownListeners);
    window.addEventListener('beforeunload', this.saveVideoCurrentTime);
    window.addEventListener(CHANGE_VIEW_EVENT, this.handleChangeVideoAiView);

    /** Start save current video time interval */
    this.saveCurrentTimeInterval = setInterval(this.saveVideoCurrentTime, 1000 * 10);
  }

  componentDidUpdate(prevProps, prevState) {
    /** reload <video> if quality or data.src changed */
    const { props, state } = this;
    if (
      (prevState.currentQuality !== null
        && prevState.currentQuality !== state.currentQuality)
      || (prevProps.data.src !== this.props.data.src && this.$video.current)
    ) {
      /** Set new src to qualities */
      if (
        props.data.src
        && prevProps.data.src !== props.data.src
        && state.qualities.length
      ) {
        const startSrc = props.data.src.slice(0, 34); // https://proxy.pics.io/googledrive/
        const qualities = state.qualities.map(
          (quality) => (quality.proxyLink.startsWith(startSrc)
            ? { ...quality, proxyLink: props.data.src }
            : quality),
        );
        this.setState({ qualities });
      }
      const $video = this.$video.current;
      const { isVideoPlaying, currentTime } = this.state;
      $video.load();
      this.setCurrentTime(currentTime);
      if (isVideoPlaying) {
        const playPromise = $video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            // Automatic playback started!
            // Show playing UI.
          })
            .catch(() => {
              // Auto-play was prevented
              // Show paused UI.
            });
        }
      }
    }

    if (
      prevProps.data._id !== props.data._id
      || prevProps.revisionsUploadedCount !== props.revisionsUploadedCount
    ) {
      this.setState(makeInitialState(props.data._id, props.data.chapters));
      this.getQualities();
    }

    /** switch asset */
    if (prevProps.data._id !== props.data._id) {
      const { currentTime } = prevState;
      this.saveVideoCurrentTime(prevProps.data._id, currentTime);

      /** This switch is needed to redraw subtitles. Without switching, subtitles are adjusted from the previous video */
      if (this.$video.current?.textTracks[0]) {
        if (state.showSubtitle) {
          this.$video.current.textTracks[0].mode = 'hidden';
          setTimeout(() => {
            this.$video.current.textTracks[0].mode = 'showing';
          }, 0);
        } else {
          this.$video.current.textTracks[0].mode = 'hidden';
        }
      }
    }

    if (prevProps.data.transcript !== props.data.transcript) {
      if (this.$video.current?.textTracks[0]) {
        this.$video.current.textTracks[0].mode = state.showSubtitle ? 'showing' : 'hidden';
      }
    }

    if (prevProps.diffData?.transcript !== props.diffData?.transcript) {
      if (this.$diffVideo.current?.textTracks[0]) {
        this.$diffVideo.current.textTracks[0].mode = state.showSubtitle ? 'showing' : 'hidden';
      }
    }

    /** switch revision */
    if (prevProps.revisionId !== props.revisionId) {
      this.setState(makeInitialState(props.data._id, props.data.chapters));
      this.getQualities();

      /** This switch is needed to redraw subtitles. Without switching, subtitles are adjusted from the previous video */
      if (this.$video.current?.textTracks[0]) {
        if (state.showSubtitle) {
          this.$video.current.textTracks[0].mode = 'hidden';
          setTimeout(() => {
            if (this.$video.current?.textTracks[0]) {
              this.$video.current.textTracks[0].mode = 'showing';
            }
          }, 0);
        } else {
          this.$video.current.textTracks[0].mode = 'hidden';
        }
      }
    }
    if (props.diffData && !prevProps.diffData) {
      this.addListenersToDiffVideo();
    }
    if (!props.diffData && prevProps.diffData) {
      this.removeListenersFromDiffVideo();
    }
    /** chapters changed */
    if (prevProps.data.chapters !== props.data.chapters) {
      if (props.data.chapters) {
        this.setState(normalizeChapters(props.data.chapters));
      } else {
        this.setState({ chapters: null });
      }
    }
  }

  componentWillUnmount() {
    this.saveVideoCurrentTime();
    this.mounted = false;
    /** remove event listeners */
    this.$video.current.removeEventListener('play', this.handlePlay);
    this.$video.current.removeEventListener('pause', this.handlePause);
    this.$video.current.removeEventListener('timeupdate', this.handleTimeUpdate);
    this.$video.current.removeEventListener('volumechange', this.handleVolumeChange);
    this.$video.current.removeEventListener('waiting', this.handleWaiting);
    this.$video.current.removeEventListener('playing', this.handlePlaying);
    this.$video.current.removeEventListener('progress', this.handleProgress);
    this.$video.current.removeEventListener('ratechange', this.handleRateChange);
    if (this.qualitiesTimeout) clearTimeout(this.qualitiesTimeout);

    document.body.removeEventListener('keydown', this.keyDownListeners);
    this.removeListenersFromDiffVideo();
    window.dispatchEvent(new CustomEvent('preview:video:pausing')); // to set default preview hotkeys
    window.removeEventListener('beforeunload', this.saveVideoCurrentTime);
    window.removeEventListener(CHANGE_VIEW_EVENT, this.handleChangeVideoAiView);

    /** Stop save current video time interval */
    if (this.saveCurrentTimeInterval !== null) {
      clearInterval(this.saveCurrentTimeInterval);
      this.saveCurrentTimeInterval = null;
    }
  }

  /**
   * Handle change Video AI view
   * @param {CustomEvent} event
   */
  handleChangeVideoAiView = (event) => {
    const view = event.detail;
    this.setState({ videoAiView: view });
  };

  getQualities = async () => {
    const { revisionId, data } = this.props;
    const { _id, storageId, mimeType } = data;
    let qualities;
    /** variable in order to set the right quality value */
    this.qualitiesFor = storageId;
    try {
      const params = [_id];
      if (revisionId) params.push(revisionId);
      qualities = await getVideoQualities(...params);
    } catch (err) {
      if (!this.mounted) return;
      if (err === QUALITIES_IN_PROGRESS) {
        if (this.qualitiesFor === storageId) {
          this.qualitiesTimeout = setTimeout(this.getQualities, 5000);
          this.setState({ qualitiesStatus: QUALITIES_STATUS.processing });
        }
        return;
      }
      this.setState({ qualitiesStatus: QUALITIES_STATUS.unavailable });

      if (mimeType !== MP4 || this.state.videoError) await this.showError(this.state.videoError);
    }

    if (this.mounted && qualities && this.qualitiesFor === storageId) {
      const originalQuality = this.state.qualities[0];
      /** if original file is broken - its has zero height size */
      let newQualities = originalQuality && originalQuality.quality !== 0
        ? [...qualities, ...this.state.qualities]
        : qualities;

      newQualities = _orderBy(_uniqBy(newQualities, 'quality'), 'quality', 'desc');

      this.setState((prevState) => ({
        qualities: newQualities,
        qualitiesStatus: QUALITIES_STATUS.initial,
        currentQuality: ((mimeType !== MP4 || prevState.videoError) && newQualities.length) ? newQualities[0].quality : prevState.currentQuality,
      }));
    }
  };

  saveVideoCurrentTime = (_id, currentTime) => {
    /** when save on window before reload/close -> first argument is `beforeunload` event */
    const id = typeof _id === 'string' ? _id : this.props.data._id;
    saveVideoCurrentTime(id, currentTime || this.state.currentTime);
  }

  handleProgress = (event) => {
    /* progress event may be triggered on the unmounted video */
    if (event.target !== this.$video.current && event.target !== this.$diffVideo.current) return;

    const isMain = event.target === this.$video.current;
    const { buffered, duration } = isMain ? this.$video.current : this.$diffVideo.current;
    const type = isMain ? 'main' : 'diff';
    const $bufferedTrack = this.$bufferedTracks[type].current;

    if (buffered && buffered.length && duration) {
      const bufferedEnd = buffered.end(0);
      const progress = (bufferedEnd / duration) * 100;

      if ($bufferedTrack) $bufferedTrack.style.width = `${progress}%`;
    }
  };

  /** @param {KeyboardEvent} event */
  keyDownListeners = (event) => {
    const { state } = this;
    const {
      keyCode, shiftKey, altKey, ctrlKey, metaKey,
    } = event;
    const isSomeInputFocused = document.activeElement
      && (document.activeElement.contentEditable === 'true'
        || ['INPUT', 'TEXTAREA'].includes(document.activeElement.nodeName.toUpperCase()));
    /** e.g. cmd + C */
    const additionalKeyPressed = shiftKey || altKey || ctrlKey || metaKey;

    if (!state.isVideoReady || isSomeInputFocused || additionalKeyPressed) return;
    event.preventDefault();

    switch (keyCode) {
    /** ArrowRight -> forward */
    case 39:
      if (state.isVideoPlaying || state.isVideoWaiting) {
        event.stopPropagation();
        const newTime = state.currentTime + 5;
        this.setIndicator('+5 sec');
        if (newTime <= state.duration) return this.setCurrentTime(newTime);
        if (state.currentTime < state.duration) this.setCurrentTime(state.duration);
      }
      return;

      /** KeyL -> forward */
    case 76: {
      const newTime = state.currentTime + 5;
      this.setIndicator('+5 sec');
      if (newTime <= state.duration) return this.setCurrentTime(newTime);
      if (state.currentTime < state.duration) this.setCurrentTime(state.duration);
      return;
    }
    /** ArrowLeft -> rewind */
    case 37:
      if (state.isVideoPlaying || state.isVideoWaiting) {
        event.stopPropagation();
        const newTime = state.currentTime - 5;
        this.setIndicator('-5 sec');
        if (newTime >= 0) return this.setCurrentTime(newTime);
        if (state.currentTime > 0) this.setCurrentTime(0);
      }
      return;

      /** KeyJ -> rewind */
    case 74: {
      const newTime = state.currentTime - 5;
      this.setIndicator('-5 sec');
      if (newTime >= 0) return this.setCurrentTime(newTime);
      if (state.currentTime > 0) this.setCurrentTime(0);
      return;
    }

    /** Spacebar | KeyK */
    case 32:
    case 75:
      this.togglePlayState();
      return;

      /** KeyF */
    case 70:
      this.toggleFullscreen();
      return;

      /** KeyM */
    case 77:
      this.toggleMuteState();
      this.setIndicator(this.state.isMuted ? 'Mute' : 'Unmute');
      return;

      /** ArrowUp */
    case 38:
      this.volumeUp();
      this.setIndicator('Volume+');
      return;

      /** ArrowDown */
    case 40:
      this.volumeDown();
      this.setIndicator('Volume-');
      return;

      /** KeyC */
    case 67:
      if (this.props.diffData) return;
      this.toggleCrop();
      this.setIndicator('Crop');
      return;

      /** KeyS */
    case 83:
      this.handleClickScreenshot();
      this.setIndicator('Create snapshot');
      return;

      /** KeyT */
    case 84:
      this.handleClickCustomThumbnail();
      this.setIndicator('Create custom thumbnail');
      // no default
    }
  };

  setIndicator = (text) => {
    if (this.indicatorTimeout) clearTimeout(this.indicatorTimeout);
    if (text) {
      this.indicatorTimeout = setTimeout(this.setIndicator, 500);
    }
    this.setState({ indicator: text });
  };

  handleCanPlay = () => {
    const { videoHeight, duration } = this.$video.current;
    const { state } = this;

    if (isNaN(duration)) return;

    const isCurrentQualityAdded = Boolean(
      state.qualities.find((item) => item.quality === videoHeight),
    );
    const qualities = isCurrentQualityAdded || !this.props.data.src || videoHeight === 0
      ? state.qualities
      : [{ quality: videoHeight, proxyLink: this.props.data.src }, ...state.qualities];

    const savedQuality = utils.LocalStorage.get('picsio.videoQuality');
    const currentQuality = _find(qualities, { quality: savedQuality });

    this.setState({
      isVideoReady: true,
      isVideoWaiting: false,
      duration: this.$diffVideo.current ? Math.max(this.state.duration, duration) : duration,
      qualities,
      currentQuality: (currentQuality && savedQuality) || state.currentQuality || (qualities[0] ? qualities[0].quality : videoHeight),
    });

    if (savedQuality && currentQuality) {
      this.$video.current.playbackRate = state.playbackRate;
    }
    this.props.onCanPlay();
  };

  handleCanPlayDiff = () => {
    this.setState({
      isDiffVideoReady: true,
      isDiffVideoWaiting: false,
      diffDuration: this.$diffVideo.current.duration,
      diffCurrentTime: this.$diffVideo.current.currentTime,
    });
  };

  handleError = async (event) => {
    const { state } = this;
    const { error } = event.currentTarget;

    const numberOfQualities = state.qualities.length;
    if (state.qualitiesStatus === QUALITIES_STATUS.unavailable) {
      await this.showError(error);
      return;
    }

    if (!numberOfQualities) {
      this.setState({ videoError: error });
      return;
    }
    if (
      numberOfQualities
      && state.currentQuality !== state.qualities[numberOfQualities - 1].quality
    ) {
      /** if can't play not last quality -> try to play next(lower) quality */
      const currentQualityIndex = state.qualities.findIndex(
        (q) => q.quality === state.currentQuality,
      );
      if (currentQualityIndex > -1) {
        const { currentQuality } = state;
        const nextQuality = state.qualities[currentQualityIndex + 1].quality;
        this.changeQuality(nextQuality, true);
        this.setIndicator(`Can not play in ${currentQuality}p, try to play ${nextQuality}p`);
      }
    } else {
      await this.showError(error);
    }
    /** otherwise -> wait for quality */
    this.setState({ videoError: error });
  };

  handlePlay = () => {
    this.setState({ isVideoPlaying: true, isDiffVideoPlaying: true, showInitialThumbnail: false });
    this.props.toggleMarkers(this.props.markers);
    window.dispatchEvent(new CustomEvent('preview:video:playing')); // to remove arrow hotkeys from preview
  };

  handlePause = () => {
    this.setState({ isVideoPlaying: false });
    window.dispatchEvent(new CustomEvent('preview:video:pausing')); // to set default preview hotkeys
  };

  handleDiffPause = () => {
    this.setState({ isDiffVideoPlaying: false });
  }

  handleTimeUpdate = (event) => {
    const { currentTime } = event.target;

    const { duration, isVideoWaiting, isVideoReady } = this.state;
    const [commentStart, commentEnd] = this.props.commentsRange;
    const isMainVideo = event.target === this.$video.current;
    const shouldSyncCommentRange = isMainVideo && !this.isDraggingCommentRange && Math.floor(commentStart) === Math.floor(commentEnd);

    if (shouldSyncCommentRange) {
      this.props.setCommentTimeRange([currentTime, currentTime]);
    }

    /**
     * for some reason in chrome timeupdate triggers several times before video is ready
     * if src on tag <video> instead of <source>
     */
    if (!isVideoReady) return;

    if (isMainVideo) {
      this.setState({
        currentTime,
        isVideoWaiting: currentTime === duration ? false : isVideoWaiting,
      });
      if (this.props.setVideoCurrentTime) {
        this.props.setVideoCurrentTime(currentTime);
      }
    } else {
      const { diffDuration, isDiffVideoWaiting } = this.state;

      this.setState({
        diffCurrentTime: currentTime,
        isDiffVideoWaiting: currentTime === diffDuration ? false : isDiffVideoWaiting,
      });
    }
  };

  handleVolumeChange = (event) => {
    /** prevent update volume when pressed "Mute" button */
    const { volume } = event.target;
    if (this.muteSetted || this.state.volume === volume) return (this.muteSetted = false);

    const muted = volume === 0;
    this.setState({
      volume,
      isMuted: muted,
    });
    LocalStorage.set('muted', muted);
    LocalStorage.set('volume', volume);
    /** remove mute property on native player if setted */
    if (!muted && this.$video.current.muted) this.$video.current.muted = false;
  };

  /** Fires when the video stops because it needs to buffer the next frame */
  handleWaiting = (event) => {
    if (event.target === this.$video.current) {
      this.setState({ isVideoWaiting: true });
    } else {
      this.setState({ isDiffVideoWaiting: true });
    }
  };

  /** Fires when the audio/video is playing after having been paused or stopped for buffering */
  handlePlaying = (event) => {
    if (event.target === this.$video.current) {
      this.setState({ isVideoWaiting: false });
    } else {
      this.setState({ isDiffVideoWaiting: false });
    }
  };

  handleRewindLeft = () => {
    const { currentTime } = this.$video.current;
    let rewindedTime;
    rewindedTime = currentTime - 2;
    rewindedTime = rewindedTime > 0 ? rewindedTime : 0;
    this.$video.current.currentTime = rewindedTime;
  }

  handleRewindRight = () => {
    const { currentTime, duration } = this.$video.current;
    let rewindedTime;
    rewindedTime = currentTime + 2;
    rewindedTime = rewindedTime < duration ? rewindedTime : duration;
    this.$video.current.currentTime = rewindedTime;
  }

  handleClickTimeline = (event) => {
    const { clientX } = event.nativeEvent;
    const isClickOnMainTimeline = this.$videoControls.current.contains(event.currentTarget);
    const { duration } = isClickOnMainTimeline ? this.$video.current : this.$diffVideo.current;
    const { left, width } = isClickOnMainTimeline
      ? this.$videoControls.current.getBoundingClientRect()
      : this.$diffVideoControls.current.getBoundingClientRect();
    const currentTime = (duration * (clientX - left)) / width;
    const mainVideoDuration = this.$video.current.duration;
    const mainCurrentTime = currentTime < mainVideoDuration ? currentTime : mainVideoDuration;

    const state = { currentTime: mainCurrentTime };
    if (this.$diffVideo.current) {
      const diffVideoDuration = this.$diffVideo.current.duration;
      const diffCurrentTime = currentTime < diffVideoDuration ? currentTime : diffVideoDuration;
      state.diffCurrentTime = diffCurrentTime;
      this.$diffVideo.current.currentTime = diffCurrentTime;
    }
    if (isFinite(mainCurrentTime)) {
      this.$video.current.currentTime = mainCurrentTime;
    }
    this.props.toggleMarkers(this.props.markers);
    this.setState(state);
    if (this.props.setVideoCurrentTime) {
      this.props.setVideoCurrentTime(mainCurrentTime);
    }
  };

  handleTimeLineHover = (event, type) => {
    const isMain = type === 'main';
    const $videoControls = isMain ? this.$videoControls.current : this.$diffVideoControls.current;
    const $video = isMain ? this.$video.current : this.$diffVideo.current;

    if (!isNaN($video.duration)) {
      const $hoveredTrack = this.$hoveredTracks[type].current;
      const $hoveredTrackTime = this.$hoveredTrackTimes[type].current;
      const controlsLeft = $videoControls.getBoundingClientRect().left;
      const width = event.clientX - controlsLeft;
      $hoveredTrack.style.width = `${width}px`;

      /** calculate time by hovered position */
      const time = ($video.duration * width) / $videoControls.offsetWidth;
      const timeText = parseTime(time);
      let chapterText = '';
      if (Array.isArray(this.state.chapters)) {
        const chapter = this.state.chapters.find((chap) => time >= chap.startTime && time <= chap.endTime);
        if (chapter) {
          chapterText = chapter.text;
        }
      }
      const text = chapterText ? `${timeText} ${chapterText}` : timeText;
      $hoveredTrackTime.innerHTML = `<span class="hoveredTimeText">${text}</span>`;

      const tooltipWidth = $hoveredTrackTime.offsetWidth;

      /** position time text */
      if (event.clientX < controlsLeft + tooltipWidth / 2) {
        $hoveredTrack.classList.add('leftPart');
      } else if (event.clientX > $videoControls.offsetWidth + controlsLeft - tooltipWidth / 2) {
        $hoveredTrack.classList.add('rightPart');
      } else {
        $hoveredTrack.classList.remove('leftPart');
        $hoveredTrack.classList.remove('rightPart');
      }
    }
  };

  togglePlayState = async () => {
    const { isVideoPlaying, isVideoWaiting, isDiffVideoWaiting } = this.state;

    if (isVideoPlaying && !this.isPlayPromiseWaiting) {
      if (this.$diffVideo.current) this.$diffVideo.current.pause();
      return this.$video.current.pause();
    }
    if (isVideoWaiting || isDiffVideoWaiting || this.isPlayPromiseWaiting) {
      return;
    }

    if (this.$diffVideo.current) {
      this.isPlayPromiseWaiting = await Promise.all([
        this.$video.current.play(),
        this.$diffVideo.current.play(),
      ]);
    } else {
      this.isPlayPromiseWaiting = await this.$video.current.play();
    }
    this.isPlayPromiseWaiting = undefined;
  };

  toggleMuteState = () => {
    this.muteSetted = true;

    const muted = !this.$video.current.muted;
    this.$video.current.muted = muted;
    this.setState({ isMuted: muted });
    LocalStorage.set('muted', muted);
  };

  toggleDiffMuteState = () => {
    const muted = !this.$diffVideo.current.muted;
    this.$diffVideo.current.muted = muted;
    this.setState({ isDiffMuted: muted });
  };

  toggleSubtitle = () => {
    const { showSubtitle } = this.state;
    const { name, _id } = this.props.data;
    if (this.$video.current?.textTracks[0] || this.$diffVideo.current?.textTracks[0]) {
      this.setState({ showSubtitle: !showSubtitle });
      LocalStorage.set('subtitle', !showSubtitle);
      Logger.log('User', `TranscriptCC${showSubtitle ? 'Off' : 'On'}`, {
        assetId: _id, assetName: name,
      });
    }
    if (this.$video.current?.textTracks[0]) {
      this.$video.current.textTracks[0].mode = !showSubtitle ? 'showing' : 'hidden';
    }
    if (this.$diffVideo.current?.textTracks[0]) {
      this.$diffVideo.current.textTracks[0].mode = !showSubtitle ? 'showing' : 'hidden';
    }
  }

  handleInputVolumeChange = (event) => {
    const { value } = event.currentTarget;
    this.setVolume(value);
  };

  volumeUp = () => {
    this.setVolume(this.$video.current.volume + 0.05);
  };

  volumeDown = () => {
    this.setVolume(this.$video.current.volume - 0.05);
  };

  setVolume = (value) => {
    if (value < 0) value = 0;
    if (value > 1) value = 1;
    this.$video.current.volume = value;
    LocalStorage.set('volume', value);
  };

  /** @param {number} time - in seconds */
  setCurrentTime = (time, setPause = false) => {
    this.props.toggleMarkers(this.props.markers);
    this.setState({ currentTime: time });
    if (this.props.setVideoCurrentTime) {
      this.props.setVideoCurrentTime(time);
    }
    this.$video.current.currentTime = time;
    if (setPause && !this.$video.current.paused) this.$video.current.pause();
  };

  handleClickScreenshot = async () => {
    Logger.log('User', 'VideoCaptureVideoScreen');
    if (!isScreenshotSupported()) return;

    const { state, props, $video } = this;
    const time = $video.current.currentTime;
    this.setState({ isScreenshotInProgress: true });
    const src = state.currentQuality
      ? state.qualities.find((item) => item.quality === state.currentQuality).proxyLink
      : props.data.src;
    const config = {
      $video: $video.current,
      src,
      time,
      type: 'blob',
      quality: 1,
    };
    const screenshot = await makeVideoScreenshot(config);

    try {
      const screenName = makeScreenshotName(this.props.data.name, time);
      utils.saveFile(screenshot, screenName);
    } catch (err) {
      showErrorDialog(localization.VIDEO.errorCreatingScreenshot);
      Logger.error(new Error('Video: can not download screenshot'), { error: err }, [
        'VideoDownloadScreenshotFailed',
        { errorMessage: (err && err.message) || 'NoMessage' },
      ]);
    }
    this.setState({ isScreenshotInProgress: false });
  };

  uploadCustomThumbnail = async (thumbnail) => {
    const {
      data: { _id: assetId },
    } = this.props;
    uiBlocker.block('Uploading...');
    await uploadThumbail(assetId, thumbnail, this.props.setCustomThumbnail);
    uiBlocker.unblock();
  };

  openCustomThumbnailDialog = (thumbnail) => {
    const img = (
      <img
        alt="New custom thumbnail"
        src={window.URL.createObjectURL(thumbnail)}
        style={{ width: '100%', height: 'auto' }}
      />
    );

    new Dialog({
      parentEl: this.$dialogWrapper.current,
      data: {
        title: 'New custom thumbnail',
        children: img,
        textBtnOk: 'Create',
        textBtnCancel: 'Cancel',
        onOk: () => this.uploadCustomThumbnail(thumbnail),
      },
    });
  };

  handleClickCustomThumbnail = async () => {
    Logger.log('User', 'VideoCreateCustomThumnail');

    if (!isScreenshotSupported()) return;

    this.setState({ isCustomThumbnailInProgress: true });

    const { props, state, $video } = this;
    const time = $video.current.currentTime;
    const src = state.currentQuality
      ? state.qualities.find((item) => item.quality === state.currentQuality).proxyLink
      : props.data.src;
    const config = {
      $video: $video.current,
      src,
      time,
      type: 'blob',
      mimeType: 'image/jpeg',
      quality: 0.8,
    };
    const screenshot = await makeVideoScreenshot(config);

    this.setState({ isCustomThumbnailInProgress: false });
    this.openCustomThumbnailDialog(screenshot);
  };

  requestFullscreen = () => {
    const $container = this.props.container || this.$container.current;
    $container.classList.add('fullScreen');
    if ($container.requestFullscreen) {
      $container.requestFullscreen();
    } else if ($container.mozRequestFullScreen) {
      $container.mozRequestFullScreen();
    } else if ($container.webkitRequestFullscreen) {
      $container.webkitRequestFullscreen();
    } else if ($container.msRequestFullscreen) {
      $container.msRequestFullscreen();
    }
  };

  exitFullscreen = () => {
    const $container = this.props.container || this.$container.current;
    $container.classList.remove('fullScreen');
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  toggleFullscreen = () => {
    if (document.fullscreenElement) this.exitFullscreen();
    else this.requestFullscreen();
  };

  toggleCrop = () => {
    Logger.log('User', 'VideoCropVideo');
    if (!picsioConfig.isMainApp || !this.props.subscriptionFeatures.trimVideo || this.props.subscriptionFeatures.planName === 'Solo') {
      Logger.log('UI', 'VideoCropVideoAvailableForPlansDialog');
      new Dialog({
        parentEl: this.$dialogWrapper.current,
        data: {
          title: 'Video Trim',
          text:
            'Video Trim is only available for “Micro”, “Small”, or “Medium” plans. Please select the plan that works best for you to use this feature.',
          textBtnCancel: null,
        },
      });
    } else {
      this.setState((prevState) => ({ showCrop: !prevState.showCrop }));
    }
  };

  handleClickMarker = (comment) => {
    const {
      _id: id, markers, videoCurrentTime, userDisplayName: userName, mentions,
    } = comment;
    const text = comment.html || comment.text;
    this.props.toggleMarkers(
      [
        {
          id,
          markers: markers.map((marker) => ({
            ...marker,
            userName,
            text,
            mentions,
          })),
        },
      ],
      {
        video: { videoCurrentTime },
      },
    );
  };

  /** Change quality
   * @param {number} quality
   */
  changeQuality = (quality, dontLog) => {
    if (!dontLog) {
      Logger.log('User', 'PlayerChangeQuality', { quality });
    }
    utils.LocalStorage.set('picsio.videoQuality', quality);
    this.setState({ currentQuality: quality });
  };

  handleRateChange = () => {
    this.setState({ playbackRate: this.$video.current.playbackRate });
  }

  changeRate = (rate) => {
    Logger.log('User', 'PlayerChangePlaybackRate', { rate });
    this.$video.current.playbackRate = rate;
  }

  /** Memoize video src */
  getCurrentSrc = () => {
    const { state, props, cachedSrcData } = this;
    const { qualities, currentQuality } = state;

    if (cachedSrcData.qualities === qualities && cachedSrcData.currentQuality === currentQuality) {
      return cachedSrcData.src;
    }

    const src = currentQuality
      ? qualities.find((item) => item.quality === currentQuality).proxyLink
      : props.data.src;
    this.cachedSrcData = {
      qualities,
      currentQuality,
      src,
      updateSrcDataTimestamp: Date.now(),
    };
    return src;
  };

  showError = async (error = {}) => {
    let { code } = error;
    if (!code) code = 'unknown';
    const explanation = MEDIA_ERRORS[code];

    if (code === 2 || (Date.now() - this.props.data.timestamp > RETRIEVE_INTERVAL) || (Date.now() - this.cachedSrcData.updateSrcDataTimestamp > RETRIEVE_INTERVAL)) {
      /** network error or expired token -> reload video */
      this.handlePause();
      const { src } = await this.props.reload();
      this.cachedSrcData.src = src;
      await this.getQualities();
      await this.togglePlayState();
      return;
    }

    Logger.error(new Error(`Can not play video: (${explanation.name})`), { error, explanation }, [
      'CanNotPlayVideo',
      {
        errorMessage: (error && error.message) || 'NoMessage',
        ...explanation,
      },
    ]);

    this.setState({
      showError: true,
      videoError: error,
      showInitialThumbnail: true,
      isVideoReady: true,
    });
  };

  getPlaybackOpenerText = (playbackRate) => {
    const playbackRateOpener = 'Speed';

    if (playbackRate === 1) {
      return playbackRateOpener;
    }
    return `${playbackRateOpener} × ${playbackRate}`;
  }

  handleTimecodeChange = (val) => {
    utils.LocalStorage.set('picsio.timecodeType', val);
    this.setState({ currentVideoTimecode: val });
    Logger.log('User', 'VideoTimecodeSelect', { type: val });
  };

  handleMouseDownCommentRangeEnd = (event) => {
    event.stopPropagation();
    this.isDraggingCommentRange = true;
    const { duration } = this.$video?.current;
    const mousemoveHandler = (e) => {
      let [start, end] = this.props.commentsRange;
      end += (e.movementX / this.$commentsRangeWrapper.current.offsetWidth)
        * this.$video?.current?.duration;
      if (end <= duration && end >= start) {
        this.setCurrentTime(end);
        this.props.setCommentTimeRange([start, end]);
      }
    };
    const mouseupHandler = () => {
      this.isDraggingCommentRange = false;
      document.body.removeEventListener('mousemove', mousemoveHandler);
      document.body.removeEventListener('mouseup', mouseupHandler);
    };

    document.body.addEventListener('mousemove', mousemoveHandler);
    document.body.addEventListener('mouseup', mouseupHandler);
  };

  handleMouseDownCommentRangeStart = (event) => {
    event.stopPropagation();
    this.isDraggingCommentRange = true;
    const mousemoveHandler = (e) => {
      let [start, end] = this.props.commentsRange;
      start += (e.movementX / this.$commentsRangeWrapper.current.offsetWidth)
        * this.$video?.current?.duration;
      if (start >= 0 && start <= end) {
        this.setCurrentTime(start);
        this.props.setCommentTimeRange([start, end]);
      }
    };
    const mouseupHandler = () => {
      this.isDraggingCommentRange = false;
      document.body.removeEventListener('mousemove', mousemoveHandler);
      document.body.removeEventListener('mouseup', mouseupHandler);
    };

    document.body.addEventListener('mousemove', mousemoveHandler);
    document.body.addEventListener('mouseup', mouseupHandler);
  };

  handleCommentMarker = (item, startKey = 'videoCurrentTime') => {
    const start = item[startKey];
    if (Array.isArray(start)) {
      return ((typeof start[0] === 'string' ? DT.t2d(start[0]) : start[0]) * 100) / this.state.duration;
    }
    return ((typeof start === 'string' ? DT.t2d(start) : start) * 100) / this.state.duration;
  };

  handleLeftStickyMarker = (item, startKey = 'videoCurrentTime') => this.$videoControls.current && (
    this.$videoControls.current.offsetWidth * (typeof item[startKey] === 'string' ? DT.t2d(item[startKey]) : item[startKey])
  ) / this.state.duration < 100

  handleRightStickyMarker = (item, startKey = 'videoCurrentTime') => this.$videoControls.current
    && (this.$videoControls.current.offsetWidth * (typeof item[startKey] === 'string' ? DT.t2d(item[startKey]) : item[startKey]))
    / this.state.duration
    > this.$videoControls.current.offsetWidth - 100

  addListenersToDiffVideo() {
    if (this.$diffVideo.current) {
      this.$diffVideo.current.addEventListener('waiting', this.handleWaiting);
      this.$diffVideo.current.addEventListener('playing', this.handlePlaying);
      this.$diffVideo.current.addEventListener('progress', this.handleProgress);
      this.$diffVideo.current.addEventListener('timeupdate', this.handleTimeUpdate);
      this.$diffVideo.current.addEventListener('pause', this.handleDiffPause);
    }
  }

  removeListenersFromDiffVideo() {
    if (this.$diffVideo.current) {
      this.$diffVideo.current.removeEventListener('waiting', this.handleWaiting);
      this.$diffVideo.current.removeEventListener('playing', this.handlePlaying);
      this.$diffVideo.current.removeEventListener('progress', this.handleProgress);
      this.$diffVideo.current.removeEventListener('timeupdate', this.handleTimeUpdate);
      this.$diffVideo.current.removeEventListener('pause', this.handleDiffPause);
    }
  }

  render() {
    const { state, props } = this;
    const volume = state.isMuted ? 0 : state.volume;
    const src = this.getCurrentSrc();
    const qualitiesList = this.memoizedNormalizeQualitiesList(state.qualities);
    const timeCodeValue = state.currentVideoTimecode;
    const $commentsRangeWrapper = this.$commentsRangeWrapper.current;

    const marginRight = $commentsRangeWrapper
      ? ($commentsRangeWrapper?.offsetWidth
        * (this.$video?.current?.duration - props?.commentsRange[1])
      ) / this.$video?.current?.duration
      : 0;
    const marginLeft = $commentsRangeWrapper
      ? ($commentsRangeWrapper?.offsetWidth
        * props?.commentsRange[0]) / this.$video?.current?.duration
      : 0;

    const [start, end] = props.commentsRange;
    const isCommentTimeEqual = Math.floor(start) === Math.floor(end);

    return (
      <div className="wrapperVideoTag" ref={this.$container}>
        <If condition={state.showError}>
          <VideoError error={state.videoError} isMobile={isMobile} />
        </If>
        {/** Main Video */}
        <div className="videoHolder">
          {props.diffData && (
            <div className="headVideo">
              <div className="titleComparison">{props.data.name}</div>
            </div>
          )}
          {props.poster && (
            <img
              alt="Poster"
              className="videoPoster"
              src={props.poster.default}
              style={{ opacity: state.showInitialThumbnail ? 1 : 0 }}
            />
          )}
          {!state.isVideoPlaying && !state.showError && (state.showInitialThumbnail || !isMobile || props.diffData) && (
            <div className="popupControl">
              <Tooltip content="Rewind (Arrow Left)" placement="top">
                <IconButton
                  componentProps={{ 'data-testid': 'actionRewindLeftButton' }}
                  className="left"
                  onClick={this.handleRewindLeft}
                  size="xxxl"
                >
                  <RewindLeft />
                </IconButton>
              </Tooltip>
              <Tooltip content="Play (Space)" placement="top">
                <div
                  className="popupPlayVideo playVideoButton"
                  onClick={this.togglePlayState}
                  role="presentation"
                />
              </Tooltip>
              <Tooltip content="Rewind (Arrow Right)" placement="top">
                <IconButton
                  componentProps={{ 'data-testid': 'actionRewindRightButton' }}
                  className="right"
                  onClick={this.handleRewindRight}
                  size="xxxl"
                >
                  <RewindRight className="iconColor" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          <video
            ref={this.$video}
            onDoubleClick={this.toggleFullscreen}
            volume={state.volume}
            muted={state.isMuted}
            preload="auto"
            crossOrigin="anonymous"
            onCanPlay={this.handleCanPlay}
            onLoadedMetadata={this.handleCanPlay}
            onError={this.handleError}
            onClick={!isMobile || props.diffData ? this.togglePlayState : null}
            style={{ opacity: state.showInitialThumbnail && props.poster ? 0 : 1, paddingBottom: '20px' }}
            playsInline
            src={src || null}
          >
            <If condition={src}>
              <source type={MP4} />
            </If>
            <If condition={props.data.transcriptLang && props.data.transcriptLink}>
              <track
                key={props.data.transcriptLink}
                label={props.data.transcriptLang}
                kind="captions"
                srcLang={props.data.transcriptLang}
                src={props.data.transcriptLink}
              />
            </If>
          </video>
        </div>
        {/** Diff Video */}
        {props.diffData && (
          <div className="videoHolder">
            <div className="headVideo">
              <div className="titleComparison">{props.diffData.name}</div>
            </div>
            {props.diffPoster && (
              <img
                alt="Poster"
                className="videoPoster"
                src={props.diffPoster.default}
                style={{ opacity: state.showInitialThumbnail ? 1 : 0 }}
              />
            )}
            <video
              ref={this.$diffVideo}
              onDoubleClick={this.toggleFullscreen}
              volume={state.volume}
              muted={state.isDiffMuted}
              preload="auto"
              crossOrigin="anonymous"
              onCanPlay={this.handleCanPlayDiff}
              onLoadedMetadata={this.handleCanPlayDiff}
              onError={this.handleError}
              onClick={!isMobile || props.diffData ? this.togglePlayState : null}
              style={{ opacity: state.showInitialThumbnail && props.diffPoster ? 0 : 1, paddingBottom: '20px' }}
              playsInline
            >
              <source type={props.diffData.mimeType} src={props.diffData.src} />
              <If condition={props.diffData.transcriptLang && props.diffData.transcriptLink}>
                <track
                  label={props.diffData.transcriptLang}
                  kind="captions"
                  srcLang={props.diffData.transcriptLang}
                  src={props.diffData.transcriptLink}
                />
              </If>
            </video>
          </div>
        )}

        {/** Controls */}
        {((state.isVideoReady && !isMobile) || (state.isVideoReady && props.diffData && isMobile)) && (
          <div
            className={cn('videoControls', {
              halfWidth: Boolean(props.diffData),
              disabled: state.showError,
              cropIsAct: state.showCrop,
            })}
            ref={this.$videoControls}
          >
            {/* tracks */}
            <div
              className="videoTracks"
              ref={this.$commentsRangeWrapper}
            >
              <If condition={!isMobile || (props.diffData && isMobile)}>
                <div
                  className="videoTracksPadding"
                  onClick={this.handleClickTimeline}
                  onMouseMove={(e) => this.handleTimeLineHover(e, 'main')}
                  role="presentation"
                >
                  <div ref={this.$hoveredTracks.main} className={cn('hoveredTrack')}>
                    <span ref={this.$hoveredTrackTimes.main} className="hoveredTime" />
                  </div>
                  <If condition={state.chapters?.length}>
                    {state.chapters.map((chapter) => (
                      <div
                        key={chapter.startTime}
                        className="chapterItem"
                        style={{
                          left: `${(chapter.startTime * 100) / (state.duration || 1)}%`,
                          width: `${((chapter.endTime - chapter.startTime) * 100) / (state.duration || 1)}%`,
                        }}
                      >
                        <div className="chapterItemTitle">{chapter.startTime}-{chapter.endTime}-{chapter.text}</div>
                      </div>
                    ))}
                  </If>
                  <If condition={this.props.isCheckedAttachTime && this.$video?.current?.paused}>
                    <div
                      className="timeRangeIndicator left"
                      style={{
                        left: marginLeft || 0,
                      }}
                    >
                      |
                    </div>
                    <If condition={!isCommentTimeEqual}>
                      <div
                        className="timeRangeIndicator right"
                        style={{
                          right: marginRight || 0,
                        }}
                      >
                        |
                      </div>
                    </If>

                  </If>
                </div>
                <div ref={this.$bufferedTracks.main} className="bufferedTrack" />
                <div
                  className="playbackTrack"
                  style={{
                    width: `${(100 * state.currentTime) / state.duration}%`,
                  }}
                />
              </If>
              {!props.diffData && props.showMarkersTrack && (
                <div className="markersTrack">
                  <If condition={props.isShowHistoryPanel}>
                    {props.revisionCommentsWithMarkers
                      && props.revisionCommentsWithMarkers.map((comment, index) => (
                        <div
                          key={index}
                          className={cn('itemMarker', {
                            leftStickyMarker: this.handleLeftStickyMarker(comment),
                            rightStickyMarker: this.handleRightStickyMarker(comment),
                          })}
                          style={{ left: `${this.handleCommentMarker(comment)}%` }}
                          onClick={() => this.handleClickMarker(comment)}
                          role="presentation"
                        >
                          <div className="itemMarkerData">
                            <div className="itemMarkerDataTitle">{comment.userDisplayName}</div>
                            <div className="itemMarkerDataText">
                              <span className="itemMarkerDataTextTime">
                                {utils.parseTime(comment.videoCurrentTime)}
                                <If condition={comment.videoCurrentTimeEnd}>
                                  - {utils.parseTime(comment.videoCurrentTimeEnd)}
                                </If>
                              </span>{' '}
                              {comment.text.replace(utils.mentionPattern, (mentionString) => {
                                const mentionID = mentionString.substring(1);
                                const mention = comment.mentions
                                  ? comment.mentions.find((mention) => mention._id === mentionID)
                                  : null;
                                return mention ? `@${mention.displayName}` : mentionString;
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                  </If>
                  <If condition={props.isShowAiPanel}>
                    <Choose>
                      <When condition={state.videoAiView === 'actions' && props.data?.actions?.length}>
                        <ActionsMarkers
                          data={props.data.actions}
                          setCurrentTime={this.setCurrentTime}
                          handleLeftStickyMarker={this.handleLeftStickyMarker}
                          handleRightStickyMarker={this.handleRightStickyMarker}
                          handleCommentMarker={this.handleCommentMarker}
                          assetId={props.data._id}
                        />
                      </When>
                      <When condition={state.videoAiView === 'ocr' && props.data?.ocr?.length}>
                        <OcrMarkers
                          data={props.data.ocr}
                          setCurrentTime={this.setCurrentTime}
                          handleLeftStickyMarker={this.handleLeftStickyMarker}
                          handleRightStickyMarker={this.handleRightStickyMarker}
                          handleCommentMarker={this.handleCommentMarker}
                          assetId={props.data._id}
                        />
                      </When>
                      <When condition={state.videoAiView === 'keywords' && props.data?.aiKeywords?.length}>
                        <KeywordsMarkers
                          data={props.data.aiKeywords}
                          setCurrentTime={this.setCurrentTime}
                          handleLeftStickyMarker={this.handleLeftStickyMarker}
                          handleRightStickyMarker={this.handleRightStickyMarker}
                          handleCommentMarker={this.handleCommentMarker}
                          assetId={props.data._id}
                        />
                      </When>
                    </Choose>
                  </If>
                </div>
              )}
            </div>
            <If condition={!props.diffData}>
              <VideoSlider
                marginLeft={marginLeft || 0}
                marginRight={marginRight || 0}
                handleMouseDownCommentRangeEnd={this.handleMouseDownCommentRangeEnd}
                handleMouseDownCommentRangeStart={this.handleMouseDownCommentRangeStart}
                isHidden={
                  !props.isCheckedAttachTime
                  || !this.$video?.current?.paused
                  || !props.isShowHistoryPanel
                }
                noProofingAccess={!picsioConfig.isMainApp && !picsioConfig?.access?.comment}
              />
            </If>
            <If condition={!isMobile || (props.diffData && isMobile)}>
              <div className="btnsVideoControls">
                <div className="btnsLeft">
                  {/* Play/Pause */}
                  <Tooltip content="Play/Pause (K)" placement="top">
                    <div
                      className={cn('btnPlayVideo', {
                        playing: state.isVideoPlaying,
                      })}
                      onClick={this.togglePlayState}
                      role="presentation"
                    />
                  </Tooltip>
                  {/* Mute/Unmute */}
                  <Tooltip content="Mute/Unmute (M)" placement="top">
                    <div className="btnVolumeVideo" onClick={this.toggleMuteState} role="presentation">
                      <Icon name={cn({ sound: !state.isMuted, soundMuted: state.isMuted })} />
                    </div>
                  </Tooltip>

                  {/* Volume */}
                  {!props.diffData && (
                    <div className="rangeVolumeVideo">
                      <input
                        type="range"
                        value={volume}
                        min="0"
                        max="1"
                        step="0.01"
                        onChange={this.handleInputVolumeChange}
                      />
                    </div>
                  )}
                  {/* Time */}
                  <div className="timeVideo">
                    <span className="timeCurrentVideo">{parseTime(this.$video.current.currentTime || state.currentTime, { type: timeCodeValue })}</span>
                    <span className="timeSeparatorVideo">/</span>
                    <span className="timeFullVideo">{parseTime(this.$video.current.duration || state.duration, { type: timeCodeValue })}</span>
                  </div>
                </div>
                <div className="btnsRight">
                  <div className="btnsThumbnailControls">
                    <If condition={!props.diffData}>
                      <PlayerThumbnailMenu
                        inProgress={state.isScreenshotInProgress || state.isCustomThumbnailInProgress}
                        onTakeScreenshot={this.handleClickScreenshot}
                        onCreateCustomThumbnail={this.handleClickCustomThumbnail}
                      />
                    </If>
                    {/* Crop */}
                    {this.isMainApp && !props.diffData && !ua.browser.isNotDesktop() && (
                      <Tooltip content="Crop video (C)" placement="top">
                        <IconButton
                          className="btnCrop"
                          onClick={this.toggleCrop}
                          id="btnCrop"
                          size="xl"
                          disabled={isNaN(state.duration)}
                        >
                          <Scissors />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  {/* Subtitles */}
                  <div className="subtitle-container">
                    <If condition={!props.data.transcript && !props.diffData && this.isMainApp}>
                      <TooltipSubtitle handleTranscriptionsPanel={props.handleTranscriptionsPanel} />
                    </If>
                    <Tooltip
                      content="Subtitles"
                      placement="top"
                    >
                      <div className={cn('btnShowSubtitle', {
                        showSubtitle: state.showSubtitle && props.data.transcript,
                        disabled: !props.data.transcript,
                      })}
                      >
                        <IconButton
                          disabled={!props.data.transcript}
                          id="btnShowSubtitle"
                          onClick={this.toggleSubtitle}
                          size="lg"
                        >
                          <Subtitle />
                        </IconButton>
                      </div>
                    </Tooltip>
                  </div>
                  <PlayerGearMenu
                    valuePlaybackRate={state.playbackRate}
                    valueQuality={state.currentQuality}
                    valueTimeCode={timeCodeValue}
                    qualityList={qualitiesList}
                    onChangePlaybackRate={this.changeRate}
                    onChangeQuality={this.changeQuality}
                    onChangeTimeCode={this.handleTimecodeChange}
                  />
                  {/* Fullscreen */}
                  {!props.diffData && !isMobile && (
                    <Tooltip
                      content={`${document.fullscreenElement ? 'Exit' : 'Enter'
                      } fullscreen mode (F)`}
                      placement="top"
                    >
                      <IconButton
                        className="btnFullscreenVideo"
                        onClick={this.toggleFullscreen}
                        id="btnFullscreenVideo"
                        size="xl"
                      >
                        <Fullscreen />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </If>

            <If condition={isMobile}>
              <div className="btnsVideoControls">
                <div className="btnsRight">
                  <If condition={!props.diffData}>
                    <MenuOnHover
                      opener={`${state.currentQuality}p`}
                      activeItem={state.currentQuality}
                      list={qualitiesList}
                      handleItemClick={this.changeQuality}
                      additionalClass="qualityVideo"
                      hideOnClick
                    />
                  </If>
                </div>
              </div>
            </If>

            {this.isMainApp && !isNaN(state.duration) && !props.diffData && state.showCrop && this.$video.current && (
              <Crop
                $video={this.$video.current}
                duration={state.duration}
                assetId={props.data._id}
                fileExtension={props.data.fileExtension}
                setCurrentTime={this.setCurrentTime}
                currentVideoTimecode={timeCodeValue}
                src={
                  state.qualities.length > 0
                    ? state.qualities[state.qualities.length - 1].proxyLink
                    : src
                }
              />
            )}
          </div>
        )}

        {/** Diff controls */}
        {props.diffData && state.isDiffVideoReady && (
          <div className="videoControls halfWidth" ref={this.$diffVideoControls}>
            {/* tracks */}
            <div className="videoTracks">
              <div
                className="videoTracksPadding"
                onClick={this.handleClickTimeline}
                onMouseMove={(e) => this.handleTimeLineHover(e, 'diff')}
                role="presentation"
              >
                <div ref={this.$hoveredTracks.diff} className={cn('hoveredTrack')}>
                  <span ref={this.$hoveredTrackTimes.diff} className="hoveredTime" />
                </div>
              </div>
              <div ref={this.$bufferedTracks.diff} className="bufferedTrack" />
              <div
                className="playbackTrack"
                style={{
                  width: `${(100 * state.diffCurrentTime) / state.diffDuration}%`,
                }}
              />
            </div>

            <div className="btnsVideoControls">
              <div className="btnsLeft">
                {/* Play/Pause */}
                <Tooltip content="Play/Pause (K)" placement="top">
                  <div
                    className={cn('btnPlayVideo', {
                      playing: state.isDiffVideoPlaying,
                    })}
                    onClick={this.togglePlayState}
                    role="presentation"
                  />
                </Tooltip>

                {/* Mute/Unmute */}
                <Tooltip content="Mute/Unmute" placement="top">
                  <div className="btnVolumeVideo" onClick={this.toggleDiffMuteState} role="presentation">
                    <Icon name={cn({ sound: !state.isDiffMuted, soundMuted: state.isDiffMuted })} />
                  </div>
                </Tooltip>
                {/* Time */}
                <div className="timeVideo">
                  <span className="timeCurrentVideo">{parseTime(this.$diffVideo?.current?.currentTime || state.diffCurrentTime, { type: timeCodeValue })}</span>
                  <span className="timeSeparatorVideo">/</span>
                  <span className="timeFullVideo">{parseTime(this.$diffVideo?.current?.duration || state.diffDuration, { type: timeCodeValue })}</span>
                </div>
              </div>
              <div className="btnsRight">
                {/* Subtitles */}
                <Tooltip
                  content="Subtitles"
                  placement="top"
                >
                  <div className={cn('btnDiffShowSubtitle', {
                    showSubtitle: state.showSubtitle && props.diffData.transcript,
                    disabled: !props.diffData.transcript,
                  })}
                  >
                    <IconButton
                      disabled={!props.diffData.transcript}
                      id="btnDiffShowSubtitle"
                      onClick={this.toggleSubtitle}
                      size="lg"
                    >
                      <Subtitle />
                    </IconButton>
                  </div>
                </Tooltip>
                {/* Fullscreen */}
                <Tooltip
                  content={`${document.fullscreenElement ? 'Exit' : 'Enter'} fullscreen mode (F)`}
                  placement="top"
                >
                  <div className="btnFullscreenVideo" onClick={this.toggleFullscreen} role="presentation">
                    <Icon name="fullscreen" />
                  </div>
                </Tooltip>
              </div>
            </div>

            {this.isMainApp && !isNaN(state.duration) && !props.diffData && state.showCrop && (
              <Crop
                $video={this.$video.current}
                duration={state.duration}
                assetId={props.data._id}
                fileExtension={props.data.fileExtension}
                setCurrentTime={this.setCurrentTime}
                currentVideoTimecode={timeCodeValue}
                src={
                  state.qualities.length > 0
                    ? state.qualities[state.qualities.length - 1].proxyLink
                    : src
                }
              />
            )}
          </div>
        )}

        {(state.isVideoWaiting || state.isDiffVideoWaiting || this.isPlayPromiseWaiting) && (
          <div className="videoIndicator buffering">Buffering...</div>
        )}
        {state.indicator && <div className="videoIndicator">{state.indicator}</div>}
        <div className="wrapperVideoDialog" ref={this.$dialogWrapper} />
        {!state.isVideoReady && <Spinner title="Loading video..." />}
      </div>
    );
  }
}

Player.propTypes = {
  container: object.isRequired, // HTMLElement
  toggleMarkers: func,
  revisionId: string,
  revisionsUploadedCount: number.isRequired,
  reload: func.isRequired,
  commentsRange: arrayOf(number),
  setVideoCurrentTime: func,
  data: object.isRequired,
  diffData: object,
  handleTranscriptionsPanel: func,
  showMarkersTrack: bool,
  isShowHistoryPanel: bool,
  isShowAiPanel: bool,
};

Player.defaultProps = {
  toggleMarkers: Function.prototype,
  handleTranscriptionsPanel: Function.prototype,
  revisionId: null,
  commentsRange: [0, 0],
  setVideoCurrentTime: null,
  diffData: null,
  showMarkersTrack: false,
};

export default Player;
